import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FieldArray, useFormikContext } from 'formik';
import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import clsx from 'clsx';

import { confirm } from '@dashboard/lib/confirm';
import { DragIcon } from '@dashboard/icons/DragIcon';

import Button, { getButtonClass } from '@dashboard/components/Button';
import FormElement from '@dashboard/components/Form/components/FormElement';
import Reorderable from '@dashboard/components/Reorderable';
import Collapsible from '@dashboard/components/Collapsible';
import Lozenge from '@dashboard/components/Lozenge';

import { Field, renderFields } from '../Form';

function OptionList({
  options = [],
  fields = [],
  allowDelete = true,
  allowAdd = true,
  allowReorder = true,
  collapseOptions = false,
  hideIfDisabled = false,
  name,
  onChange,
  optionShape = {},
  addOptionLabel = '',
  deleteOptionLabel = 'Delete option?',
}) {
  const { values, setFieldValue } = useFormikContext();
  const [openItems, setOpenItems] = useState({});

  const OPTION = {
    key: uniqueId(),
    ...optionShape,
  };

  return (
    <FormElement>
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <>
            <Reorderable
              id={`fields.${name}`}
              items={options}
              onReorder={items => {
                onChange(items);
              }}
              renderItem={({ dragHandleProps, index }) => {
                if (collapseOptions) {
                  const isOpen = openItems[index];

                  return (
                    <Reorderable.Item
                      className={clsx({
                        hidden: hideIfDisabled && !options[index].enabled,
                      })}
                    >
                      <Collapsible.Root
                        open={openItems[index]}
                        onOpenChange={open => setOpenItems(prev => ({ ...prev, [index]: open }))}
                      >
                        <div className="w-full sticky top-0 bg-white z-10">
                          <div className="flex flex-row gap-2 items-center justify-start">
                            <div {...dragHandleProps}>
                              <DragIcon className="w-4 h-4" />
                            </div>

                            <Collapsible.Trigger
                              className={clsx(
                                `py-3 font-medium truncate w-full text-left flex items-center justify-between !ring-0 !focus:ring-0`,
                                getButtonClass('bare'),
                              )}
                              disabled={!options[index].enabled}
                              type="button"
                            >
                              <div className="inline-flex gap-x-2 items-center truncate">
                                <div className="truncate">
                                  {options[index].name}{' '}
                                  <span>{options[index].required ? '*' : ''}</span>
                                  {options[index].hidden && (
                                    <Lozenge className="ml-2">Hidden</Lozenge>
                                  )}
                                </div>
                              </div>
                              <span>
                                {isOpen ? (
                                  <ChevronUpIcon className="w-4 h-4" />
                                ) : (
                                  <ChevronDownIcon className="w-4 h-4" />
                                )}
                              </span>
                            </Collapsible.Trigger>

                            {allowDelete && (
                              <Button
                                handleClick={async () => {
                                  const del = await confirm(`${deleteOptionLabel}`, {
                                    confirm: 'Delete',
                                  });

                                  if (del) {
                                    arrayHelpers.remove(index);
                                  }
                                }}
                                appearance="icon-button"
                                size="narrow"
                                type="button"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </Button>
                            )}
                          </div>

                          <Collapsible.Content className={clsx(`pb-4 pt-2 relative`)}>
                            <div className="flex-1 mr-2 min-w-0">
                              {renderFields({
                                fields:
                                  typeof fields === 'function'
                                    ? fields({ index, values: values[name][index] })
                                    : fields,
                                values,
                                setFieldValue,
                              }).map(field => (
                                <Field
                                  key={field.name}
                                  values={values}
                                  field={{
                                    ...field,
                                    id: `${name}.${index}.${field.name}`,
                                    name: `${name}.${index}.${field.name}`,
                                    visible: field.visible === undefined ? true : field.visible,
                                    reduceMargin: true,
                                    ...(field.fields?.length > 0
                                      ? {
                                          fields: field.fields.map(subField => ({
                                            ...subField,
                                            id: `${name}.${index}.${subField.name}`,
                                            name: `${name}.${index}.${subField.name}`,
                                          })),
                                        }
                                      : {}),
                                  }}
                                />
                              ))}
                            </div>
                          </Collapsible.Content>
                        </div>
                      </Collapsible.Root>
                    </Reorderable.Item>
                  );
                }

                return (
                  <div
                    className={clsx('flex flex-row justify-start align-top items-start mb-4', {
                      hidden: hideIfDisabled && !options[index].enabled,
                    })}
                  >
                    <div {...dragHandleProps} className="pt-2 pr-2">
                      <DragIcon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 mr-2 min-w-0">
                      {renderFields({
                        fields:
                          typeof fields === 'function'
                            ? fields({ index, values: values[name][index] })
                            : fields,
                        values,
                        setFieldValue,
                      }).map(field => (
                        <Field
                          key={field.name}
                          values={values}
                          field={{
                            ...field,
                            id: `${name}.${index}.${field.name}`,
                            name: `${name}.${index}.${field.name}`,
                            visible: field.visible === undefined ? true : field.visible,
                            reduceMargin: true,
                            ...(field.fields?.length > 0
                              ? {
                                  fields: field.fields.map(subField => ({
                                    ...subField,
                                    id: `${name}.${index}.${subField.name}`,
                                    name: `${name}.${index}.${subField.name}`,
                                  })),
                                }
                              : {}),
                          }}
                        />
                      ))}
                    </div>

                    {allowDelete && (
                      <Button
                        handleClick={async () => {
                          const del = await confirm(`${deleteOptionLabel}`, {
                            confirm: 'Delete',
                          });

                          if (del) {
                            arrayHelpers.remove(index);
                          }
                        }}
                        appearance="icon-button"
                        size="narrow"
                        type="button"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              }}
            />

            {allowAdd && (
              <Button
                handleClick={() => arrayHelpers.push(OPTION)}
                appearance="light"
                type="button"
              >
                {addOptionLabel}
              </Button>
            )}
          </>
        )}
      />
    </FormElement>
  );
}

export default OptionList;
